import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  InputAdornment,

} from '@mui/material';
import { useAuth } from '../../hooks/useAuth';
import AccountCreationForm from './AccountCreationForm';

interface LoginDialogProps {
  open: boolean;
  onClose: () => void;
}

export const LoginDialog: React.FC<LoginDialogProps> = ({ open, onClose }) => {
  const { login, verifyOtpAndLogin } = useAuth();
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [showAccountCreation, setShowAccountCreation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSendOtp = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await login({ mobile });
      
      if (response.data?.already_exists === false) {
        // Show account creation form for new users
        setShowAccountCreation(true);
      } else {
        // Existing user, proceed with OTP
        setError(response.message);
        setIsOtpSent(true);
      }
    } catch (err: any) {
      console.error('Send OTP error:', err);
      setError(err?.response?.data?.message || err?.message || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    try {
      setLoading(true);
      setError(null);
      await verifyOtpAndLogin({ mobile, otp });
      onClose();
    } catch (err: any) {
      console.error('Verify OTP error:', err);
      setError(err?.response?.data?.message || 'Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setMobile('');
    setOtp('');
    setIsOtpSent(false);
    setShowAccountCreation(false);
    setError(null);
    onClose();
  };

  const handleAccountCreated = () => {
    // Reset form and show OTP screen
    setShowAccountCreation(false);
    setIsOtpSent(true);
    setError('Account created successfully! Please verify your mobile with OTP.');
  };

  const validateMobile = (value: string) => {
    return /^[6-9]\d{9}$/.test(value);
  };

  const validateOtp = (value: string) => {
    return /^\d{4,6}$/.test(value);
  };

  if (showAccountCreation) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Create Account</DialogTitle>
        <DialogContent>
          <AccountCreationForm 
            mobile={mobile} 
            onSuccess={handleAccountCreated} 
          />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Login with Mobile</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          {error && (
            <Typography color={error.startsWith('Error') ? 'error' : 'success'} sx={{ mb: 2 }}>
              {error}
            </Typography>
          )}
          {!isOtpSent ? (
            <TextField
              autoFocus
              margin="dense"
              label="Mobile Number"
              type="tel"
              fullWidth
              value={mobile}
              onChange={(e) => setMobile(e.target.value.slice(0, 10))}
              disabled={loading}
              error={mobile.length > 0 && !validateMobile(mobile)}
              helperText={mobile.length > 0 && !validateMobile(mobile) ? 'Enter a valid 10-digit mobile number' : ''}
              InputProps={{
                startAdornment: <InputAdornment position="start">+91</InputAdornment>,
              }}
              inputProps={{
                maxLength: 10,
                pattern: '[6-9]{1}[0-9]{9}',
              }}
            />
          ) : (
            <>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                OTP sent to +91 {mobile}
              </Typography>
              <TextField
                autoFocus
                margin="dense"
                label="Enter OTP"
                type="number"
                fullWidth
                value={otp}
                onChange={(e) => setOtp(e.target.value.slice(0, 6))}
                disabled={loading}
                error={otp.length > 0 && !validateOtp(otp)}
                helperText={otp.length > 0 && !validateOtp(otp) ? 'Enter a valid OTP' : ''}
                inputProps={{
                  maxLength: 6,
                  pattern: '[0-9]*',
                }}
              />
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={isOtpSent ? handleVerifyOtp : handleSendOtp}
          variant="contained"
          disabled={
            loading || 
            (!isOtpSent && !validateMobile(mobile)) || 
            (isOtpSent && !validateOtp(otp))
          }
        >
          {loading ? (
            <CircularProgress size={24} />
          ) : isOtpSent ? (
            'Verify OTP'
          ) : (
            'Send OTP'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
